import React from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@core-ui/Components/ui/dialog.tsx';
import { Button } from '@core-ui/Components/ui/button.tsx';
import { type NumbersForm } from '@types';
import { cn } from '@core-ui-lib/utils.ts';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@core-ui/Components/ui/checkbox.tsx';
import { IconPhone } from '@tabler/icons-react';

interface Props {
  open: boolean;
  setOpen: (v: boolean) => void;
  form: NumbersForm;
  setForm: (f: NumbersForm) => void;
  onSave: () => void;
  errors?: Partial<Record<
    'number' | 'active',
    string
  >>;
  clearError?: (field: 'number' | 'active' ) => void;
}

export default function CallerIdDialog ({ open, setOpen, form, setForm, onSave, errors = {},
}: Props): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={setOpen} modal={true}>
      <DialogContent
        onOpenAutoFocus={(event) => {
          event.preventDefault();
        }}
        className={cn(
          'rounded-2xl shadow-2xl w-full max-w-lg',
          'bg-card text-foreground border border-border',
          'data-[state=open]:animate-in data-[state=open]:fade-in-0',
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
          '[&_div[data-radix-dialog-overlay]]:bg-black/60',
          '[&_div[data-radix-dialog-overlay]]:backdrop-blur-sm'
        )}
      >

        <DialogHeader className="mb-2">
          <DialogTitle className="text-xl font-semibold text-left">
            {t('adminConsole.numberDialog.edit', 'Edit Number')}
          </DialogTitle>
          <DialogDescription>
            {t('settings.callerId.edit.description')}
          </DialogDescription>
        </DialogHeader>
        
        <div className="rounded-md border border-border p-3">
          <div className="flex justify-center items-center">
            <IconPhone className="mr-4 h-4 w-4"/>
            <label className="text-sm font-medium block text-muted-foreground">
              {t('adminConsole.numberDialog.active', 'Active')}
            </label>
            <Checkbox data-qa-id="active-number" className={'flex ml-4'} checked={!(form.active === false)} onCheckedChange={(checked) => {
              setForm({ ...form, active: checked === true });
            }}>
            </Checkbox>
          </div>
        </div>

        <div className="flex justify-end mt-6 gap-4">
          <Button
            data-qa-id='cancel-edit-number-button'
            variant="outline"
            onClick={() => {
              setOpen(false);
            }}
            className="border border-border text-foreground hover:bg-muted"
          >
            {t('adminConsole.numberDialog.cancel', 'Cancel')}
          </Button>
          <Button
            data-qa-id='save-edit-number-button'
            onClick={onSave}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {t('adminConsole.numberDialog.save', 'Save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
